/**
 * Projects Page - Main dashboard
 * Displays project cards with nested view rows
 * Phase 2: Token authentication implementation
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Typography,
  Card,
  Empty,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Segmented,
  Dropdown,
  Tag,
  Space,
  Spin,
  Skeleton,
} from 'antd';
import { App } from 'antd';
import type { MenuProps } from 'antd';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { Project, View, TimerSettings, CameraSettings, Photo } from '@/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  createProject,
  getProjects,
  deleteProject,
  updateProject,
} from '@/services/projectService';
import {
  createView,
  getViewsByProject,
  subscribeToViews,
  subscribeToLatestPhoto,
  regenerateToken,
  deleteView,
  updateTimerSettings,
  updateCameraSettings,
  updateBulkTimerSettings,
  updateViewName,
  moveViewToProject,
} from '@/services/viewService';
import { formatTokenDisplay } from '@/utils/token';
import { copyToClipboard } from '@/utils/clipboard';
import AppLayout from '@/components/AppLayout';
import { useWhatsAppViewMute } from '@/hooks/useWhatsAppViewMute';
import { useUserShares } from '@/hooks/useUserShares';
import { WhatsAppSettings } from '@/components/WhatsAppSettings';
import { TestShotViewer } from '@/components/TestShotViewer';
import ShareDialog from '@/components/ShareDialog';

import {
  OfflineTag,
  SurvivalTag,
  UnpairedTag,
} from '@/components/DeviceStatusTags';
import { StatusDashboard, StatusDetailDrawer } from '@/components/StatusDashboard';
import { SettingsDrawer } from '@/components/SettingsDrawer';
import type { HistoryEventType } from '@/types/history';
import { formatTimeAgo } from '@/utils/dateUtils';
import { useConnectionStatus } from '@/hooks/useConnectionStatus';
import { MODAL_BLUR_STYLES } from '@/styles/modalStyles';

const { Title, Text } = Typography;


/**
 * Check if the last capture is stale based on timer settings and time ranges.
 * Returns true if the last capture is too old (device should have captured but didn't).
 */
function isLastCaptureStale(view: View, lastCaptureTime: Date | null): boolean {
  // If not capturing, never stale
  if (!view.capturing) return false;

  // If no timer settings or no last capture, can't determine staleness
  const timerSettings = view.timerSettings;
  if (!timerSettings || !lastCaptureTime) return false;

  const now = new Date();
  const intervalMs = timerSettings.interval * 1000;
  const staleThresholdMs = intervalMs * 2;

  // With no schedule restriction, just check the time difference
  if (!timerSettings.scheduleEnabled) {
    return (now.getTime() - lastCaptureTime.getTime()) > staleThresholdMs;
  }

  // Check if we're currently within an active time range
  const currentDay = now.getDay(); // 0-6, Sunday=0
  const currentTime = now.toTimeString().slice(0, 5); // "HH:mm"

  let isWithinActiveRange = false;

  if (timerSettings.scheduleMode === 'advanced' && timerSettings.dailySchedules) {
    // Advanced mode: check per-day schedules
    const todaySchedule = timerSettings.dailySchedules.find(s => s.day === currentDay);
    if (todaySchedule?.enabled && todaySchedule.timeRanges) {
      isWithinActiveRange = todaySchedule.timeRanges.some(range =>
        currentTime >= range.start && currentTime <= range.end
      );
    }
  } else {
    // Basic mode: check daysOfWeek and timeRanges
    const isDayActive = timerSettings.daysOfWeek?.includes(currentDay) ?? false;
    if (isDayActive && timerSettings.timeRanges) {
      isWithinActiveRange = timerSettings.timeRanges.some(range =>
        currentTime >= range.start && currentTime <= range.end
      );
    }
  }

  // If we're outside active time ranges, the capture is not stale
  if (!isWithinActiveRange) return false;

  // We're within an active time range, check if the capture is too old
  return (now.getTime() - lastCaptureTime.getTime()) > staleThresholdMs;
}

/**
 * Format interval in seconds to a human-readable string
 */
function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

/**
 * Check if time ranges cover the full day (00:00 to 23:59 or 24:00)
 */
function isFullDayCoverage(timeRanges?: { start: string; end: string }[]): boolean {
  if (!timeRanges || timeRanges.length === 0) return false;
  return timeRanges.some(r => r.start === '00:00' && (r.end === '23:59' || r.end === '24:00'));
}

/**
 * Check if scheduling has restrictions (not 24/7).
 * Returns true if there's a schedule that limits capture times.
 */
function hasSchedulingRestrictions(timerSettings?: TimerSettings): boolean {
  if (!timerSettings) return false;

  // No schedule means no restrictions
  if (!timerSettings.scheduleEnabled) return false;

  // Check based on schedule mode
  if (timerSettings.scheduleMode === 'advanced' && timerSettings.dailySchedules) {
    // Advanced mode: check if all 7 days enabled with full coverage
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    const allDaysFullCoverage = allDays.every(day => {
      const schedule = timerSettings.dailySchedules!.find(s => s.day === day);
      return schedule?.enabled && isFullDayCoverage(schedule.timeRanges);
    });
    return !allDaysFullCoverage;
  } else {
    // Basic mode: check if all 7 days AND full time coverage
    const allDaysSelected = timerSettings.daysOfWeek?.length === 7;
    const fullTimeCoverage = isFullDayCoverage(timerSettings.timeRanges);
    return !(allDaysSelected && fullTimeCoverage);
  }
}
const { Meta } = Card;

function ViewCardDescription({ view, onOfflineClick, onUnpairedClick, onCapturingClick, onSurvivalClick, onIconClick, onCameraSettingsClick }: {
  view: View;
  onOfflineClick: () => void;
  onUnpairedClick: () => void;
  onCapturingClick: () => void;
  onSurvivalClick: () => void;
  onIconClick: (itemId: string) => void;
  onCameraSettingsClick: () => void;
}) {
  const status = useConnectionStatus(view.token, view.androidStatus);

  if (status !== 'online' && status !== 'survival') {
    return (
      <div className="mt-1">
        {status === 'offline' && <OfflineTag onClick={(e) => { e.stopPropagation(); onOfflineClick(); }} />}
        {(status === 'unpaired' || status === 'never_connected') && (
          <UnpairedTag onClick={(e) => { e.stopPropagation(); onUnpairedClick(); }} />
        )}
      </div>
    );
  }

  const cameraMode = view.settings?.camera?.mode ?? 'internal';

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {status === 'survival' ? (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
          onClick={onSurvivalClick}
        >
          <SurvivalTag />
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
            {view.androidStatus?.device?.batteryLevel != null && `${view.androidStatus.device.batteryLevel}%`}
            {view.androidStatus?.lastSeen && (
              <> — {formatTimeAgo(view.androidStatus.lastSeen.toDate())}</>
            )}
          </span>
        </div>
      ) : (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', color: view.capturing ? '#52c41a' : '#faad14', cursor: 'pointer' }}
          onClick={onCapturingClick}
        >
          {view.capturing ? (
            <>
              <MaterialIcon name="play_circle" size={18} />
              {view.timerSettings
                ? `Capturing ${formatInterval(view.timerSettings.interval)}${hasSchedulingRestrictions(view.timerSettings) ? ' + Scheduling' : ''}`
                : 'Capturing — interval not configured'}
            </>
          ) : (
            <>
              <MaterialIcon name="pause_circle" size={18} />
              Capture paused
            </>
          )}
        </div>
      )}
      <div style={{ paddingTop: 8 }}>
        <StatusDashboard
          androidStatus={view.androidStatus}
          cameraSettings={view.settings?.camera}
          cameraMode={cameraMode}
          gpsLocation={view.gpsLocation}
          gpsError={view.gpsError}
          isOnline={status === 'online'}
          masterPower={view.masterPower}
          onIconClick={onIconClick}
          onCameraSettingsClick={onCameraSettingsClick}
        />
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const { effectiveUid } = useEffectiveUserId();
  const navigate = useNavigate();
  const { isMuted, setMuted, isConfigured: isWhatsappConfigured } = useWhatsAppViewMute();
  const { isViewShared } = useUserShares();
  const [whatsappSettingsOpen, setWhatsappSettingsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { modal } = App.useApp();
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewsByProject, setViewsByProject] = useState<Record<string, View[]>>({});
  const [loading, setLoading] = useState(true);
  const [createProjectModalVisible, setCreateProjectModalVisible] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [createViewModalVisible, setCreateViewModalVisible] = useState(false);
  const [creatingView, setCreatingView] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [testShotView, setTestShotView] = useState<View | null>(null);
  const [newViewToken, setNewViewToken] = useState<string | null>(null);

  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [activeDrawerTab, setActiveDrawerTab] = useState<string>('1');
  const [historyFilterType, setHistoryFilterType] = useState<HistoryEventType | 'all'>('all');
  const [latestPhotos, setLatestPhotos] = useState<Record<string, Photo | null>>({});
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const [form] = Form.useForm();
  // Bulk Actions modal state
  const [bulkActionsModalVisible, setBulkActionsModalVisible] = useState(false);
  const [bulkActionsProject, setBulkActionsProject] = useState<Project | null>(null);
  const [bulkCapturing, setBulkCapturing] = useState<'unchanged' | 'enabled' | 'paused'>('unchanged');
  const [bulkIntervalValue, setBulkIntervalValue] = useState<number | undefined>(undefined);
  const [bulkIntervalUnit, setBulkIntervalUnit] = useState<number>(60); // default to minutes
  const [bulkRawCapture, setBulkRawCapture] = useState<'unchanged' | 'enabled' | 'disabled'>('unchanged');
  const [bulkActionsSaving, setBulkActionsSaving] = useState(false);
  // Edit View modal state
  const [editViewModalVisible, setEditViewModalVisible] = useState(false);
  const [viewToEdit, setViewToEdit] = useState<View | null>(null);
  const [editViewForm] = Form.useForm();
  // Move View modal state
  const [moveViewModalVisible, setMoveViewModalVisible] = useState(false);
  const [viewToMove, setViewToMove] = useState<View | null>(null);
  const [moveTargetProjectId, setMoveTargetProjectId] = useState<string | null>(null);
  const [movingView, setMovingView] = useState(false);
  // Edit Project modal state
  const [editProjectModalVisible, setEditProjectModalVisible] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [editProjectForm] = Form.useForm();
// Status Detail Drawer state
  const [statusDrawerVisible, setStatusDrawerVisible] = useState(false);
  const [statusDrawerView, setStatusDrawerView] = useState<View | null>(null);
  const [statusDrawerScrollTarget, setStatusDrawerScrollTarget] = useState<string | null>(null);
  // Highlight field state - used to scroll + flash specific fields when clicking tags
  const [highlightFieldId, setHighlightFieldId] = useState<string | null>(null);

  // Share dialog state
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [shareDialogProject, setShareDialogProject] = useState<Project | null>(null);

  const [whatsappToggleView, setWhatsappToggleView] = useState<View | null>(null);

  // Controlled state for three-dots menus: tracks which Dropdown is open (by id).
  // Used to close any open menu when the user starts scrolling the page,
  // so the menu does not remain floating after its trigger has left the viewport.
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Keep status drawer view in sync with live Firestore data so toggles
  // and heartbeat updates are reflected while the drawer is open
  useEffect(() => {
    if (!statusDrawerVisible || !statusDrawerView) return;
    const views = viewsByProject[statusDrawerView.projectId];
    if (!views) return;
    const liveView = views.find(v => v.viewId === statusDrawerView.viewId);
    if (liveView) setStatusDrawerView(liveView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewsByProject, statusDrawerVisible]);

  // Close any open three-dots Dropdown as soon as the user scrolls.
  // Listener is registered in capture phase to catch scroll on any scrollable ancestor.
  useEffect(() => {
    if (!openDropdownId) return;
    const handleScroll = () => setOpenDropdownId(null);
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true } as EventListenerOptions);
    };
  }, [openDropdownId]);

  useEffect(() => {
    if (effectiveUid) {
      loadProjects();
    }
  }, [effectiveUid]);

  // Set up real-time listeners for views
  useEffect(() => {
    if (!effectiveUid || projects.length === 0) return;

    const unsubscribers: (() => void)[] = [];

    // Subscribe to each project's views
    projects.forEach((project) => {
      const unsubscribe = subscribeToViews(
        effectiveUid,
        project.projectId,
        (views) => {
          setViewsByProject((prev) => ({
            ...prev,
            [project.projectId]: views,
          }));
        },
        (error) => {
          console.error(`Failed to listen to views for project ${project.projectId}:`, error);
        }
      );
      unsubscribers.push(unsubscribe);
    });

    // Cleanup function to unsubscribe from all listeners
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [effectiveUid, projects]);

  // Update selectedView when viewsByProject changes (to sync real-time updates)
  // Use selectedView.viewId in dependency to avoid unnecessary updates
  const selectedViewId = selectedView?.viewId;
  useEffect(() => {
    if (!selectedViewId) return;

    // Find the updated view in viewsByProject
    for (const views of Object.values(viewsByProject)) {
      const updatedView = views.find(v => v.viewId === selectedViewId);
      if (updatedView) {
        setSelectedView(updatedView);
        break;
      }
    }
  }, [viewsByProject, selectedViewId]);

  // Handle deep link URL params (viewId, tab) - auto-open drawer when navigating from Android app
  useEffect(() => {
    const viewId = searchParams.get('viewId');
    const tab = searchParams.get('tab');

    if (!viewId || Object.keys(viewsByProject).length === 0) return;

    // Find the view in loaded views
    let foundView: View | null = null;
    for (const views of Object.values(viewsByProject)) {
      const view = views.find(v => v.viewId === viewId);
      if (view) {
        foundView = view;
        break;
      }
    }

    if (foundView) {
      // Open the drawer with the specified view and tab
      setSelectedView(foundView);
      setSettingsDrawerVisible(true);

      // Map tab name to tab key
      const tabKey = tab === 'camera' ? '1' : '2';
      setActiveDrawerTab(tabKey);

      // Clear URL params after handling
      setSearchParams({}, { replace: true });
    } else {
      // View not found - silently clear params and show projects page
      setSearchParams({}, { replace: true });
    }
  }, [viewsByProject, searchParams, setSearchParams]);

  // Extract stable view IDs to prevent subscription recreation on every viewsByProject update
  // Only recreate subscriptions when the actual list of view IDs changes
  const viewIds = useMemo(() => {
    const allViews = Object.values(viewsByProject).flat();
    return allViews.map(v => v.viewId).sort().join(',');
  }, [viewsByProject]);

  // Track active subscriptions to prevent recreation
  const photoSubscriptionsRef = useRef<Map<string, () => void>>(new Map());

  // Safety valve for the loading gate: never hold the skeleton longer than 4s
  // (slow networks, degenerate states)
  const [forceReveal, setForceReveal] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setForceReveal(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  // Subscribe to latest photo for each view
  // Uses stable viewIds string to prevent unnecessary subscription recreation
  useEffect(() => {
    if (!effectiveUid) return;

    const currentViewIds = new Set(viewIds.split(',').filter(Boolean));
    const existingViewIds = new Set(photoSubscriptionsRef.current.keys());

    // Unsubscribe from views that no longer exist
    for (const viewId of existingViewIds) {
      if (!currentViewIds.has(viewId)) {
        const unsub = photoSubscriptionsRef.current.get(viewId);
        if (unsub) unsub();
        photoSubscriptionsRef.current.delete(viewId);
      }
    }

    // Subscribe to new views
    for (const viewId of currentViewIds) {
      if (!existingViewIds.has(viewId) && viewId) {
        const unsubscribe = subscribeToLatestPhoto(
          effectiveUid,
          viewId,
          (photo) => {
            setLatestPhotos((prev) => {
              // Check if this is a new photo (different photoId)
              const prevPhoto = prev[viewId];
              const isNewPhoto = photo && (!prevPhoto || prevPhoto.photoId !== photo.photoId);
              if (isNewPhoto) {
                // Mark as loading when a new photo arrives
                setImageLoading((loadingState) => ({
                  ...loadingState,
                  [viewId]: true,
                }));
              }
              return {
                ...prev,
                [viewId]: photo,
              };
            });
            // Reset error state when new photo arrives
            setImageLoadErrors((prev) => ({
              ...prev,
              [viewId]: false,
            }));
          },
          (error) => {
            console.error(`Failed to listen to latest photo for view ${viewId}:`, error);
          }
        );
        photoSubscriptionsRef.current.set(viewId, unsubscribe);
      }
    }

    // Cleanup on unmount
    return () => {
      photoSubscriptionsRef.current.forEach((unsub) => unsub());
      photoSubscriptionsRef.current.clear();
    };
  }, [effectiveUid, viewIds]);

  const loadProjects = async () => {
    if (!effectiveUid) return;

    try {
      setLoading(true);
      const projectsData = await getProjects(effectiveUid);
      setProjects(projectsData);

      // Initial load of views (will be replaced by real-time listeners)
      const viewsData: Record<string, View[]> = {};
      for (const project of projectsData) {
        const views = await getViewsByProject(effectiveUid, project.projectId);
        viewsData[project.projectId] = views;
      }
      setViewsByProject(viewsData);
    } catch (error) {
      console.error('Failed to load projects:', error);
      message.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Create project
  const handleCreateProject = async (values: { name: string }) => {
    if (!effectiveUid || creatingProject) return;

    setCreatingProject(true);
    try {
      await createProject(effectiveUid, values);
      message.success('Project created successfully');
      setCreateProjectModalVisible(false);
      form.resetFields();
      loadProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
      message.error('Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  // Create view
  const handleCreateView = async (values: { name: string }) => {
    if (!effectiveUid || !selectedProjectId || creatingView) return;

    setCreatingView(true);
    try {
      const { token } = await createView(effectiveUid, selectedProjectId, values);
      setCreateViewModalVisible(false);
      form.resetFields();
      setNewViewToken(token);
      loadProjects();
    } catch (error: unknown) {
      console.error('Failed to create view:', error);
      // Check for view limit error
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.startsWith('VIEW_LIMIT_REACHED:')) {
        const [, , limit] = errorMessage.split(':');
        message.error(
          `You've reached the maximum of ${limit} views for your plan. ` +
          `Upgrade to create more views.`
        );
      } else {
        message.error('Failed to create view');
      }
    } finally {
      setCreatingView(false);
    }
  };

  // Copy token to clipboard
  const handleCopyToken = async (token: string) => {
    const ok = await copyToClipboard(token);
    if (ok) message.success('Token copied to clipboard');
    else message.error('Failed to copy token');
  };

  // Regenerate token
  const handleRegenerateToken = async (
    userId: string,
    projectId: string,
    viewId: string
  ) => {
    modal.confirm({
      title: 'Regenerate Token',
      content: 'The current device will be unpaired and will need to enter the new token to reconnect. Continue?',
      okText: 'Regenerate',
      okType: 'danger',
      styles: MODAL_BLUR_STYLES,
      onOk: async () => {
        try {
          const newToken = await regenerateToken(userId, projectId, viewId);
          message.success(`New token generated: ${formatTokenDisplay(newToken)}`);
          loadProjects();
        } catch (error) {
          console.error('Failed to regenerate token:', error);
          message.error('Failed to regenerate token');
        }
      },
    });
  };

  // Edit view name
  const handleEditViewSubmit = async (values: { name: string }) => {
    if (!viewToEdit || !effectiveUid) return;

    try {
      await updateViewName(effectiveUid, viewToEdit.projectId, viewToEdit.viewId, values.name);
      message.success('View renamed');
      setEditViewModalVisible(false);
      setViewToEdit(null);
      editViewForm.resetFields();
    } catch (error) {
      console.error('Failed to rename view:', error);
      message.error('Failed to rename view');
    }
  };

  // Move view to another project
  const handleMoveView = async () => {
    if (!effectiveUid || !viewToMove || !moveTargetProjectId) return;

    setMovingView(true);
    try {
      await moveViewToProject(effectiveUid, viewToMove.projectId, moveTargetProjectId, viewToMove.viewId);
      message.success('View moved successfully');
      setMoveViewModalVisible(false);
      setViewToMove(null);
      setMoveTargetProjectId(null);
      loadProjects();
    } catch (error) {
      console.error('Failed to move view:', error);
      message.error('Failed to move view');
    } finally {
      setMovingView(false);
    }
  };

  // Edit project name
  const handleEditProjectSubmit = async (values: { name: string }) => {
    if (!projectToEdit || !effectiveUid) return;

    try {
      await updateProject(effectiveUid, projectToEdit.projectId, { name: values.name });
      setProjects(prev => prev.map(p =>
        p.projectId === projectToEdit.projectId ? { ...p, name: values.name } : p
      ));
      message.success('Project renamed');
      setEditProjectModalVisible(false);
      setProjectToEdit(null);
      editProjectForm.resetFields();
    } catch (error) {
      console.error('Failed to rename project:', error);
      message.error('Failed to rename project');
    }
  };

  // Delete view
  const handleDeleteView = async (
    userId: string,
    projectId: string,
    viewId: string,
    viewName: string
  ) => {
    modal.confirm({
      title: 'Delete View',
      content: `Are you sure you want to delete "${viewName}"? All photos will be deleted.`,
      okText: 'Delete',
      okType: 'danger',
      styles: MODAL_BLUR_STYLES,
      onOk: async () => {
        try {
          await deleteView(userId, projectId, viewId);
          message.success('View deleted successfully');
          loadProjects();
        } catch (error) {
          console.error('Failed to delete view:', error);
          message.error('Failed to delete view');
        }
      },
    });
  };

  // Delete project
  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (!effectiveUid) return;

    modal.confirm({
      title: 'Delete Project',
      content: `Are you sure you want to delete "${projectName}"? All views and photos will be deleted.`,
      okText: 'Delete',
      okType: 'danger',
      styles: MODAL_BLUR_STYLES,
      onOk: async () => {
        try {
          await deleteProject(effectiveUid, projectId);
          message.success('Project deleted successfully');
          loadProjects();
        } catch (error) {
          console.error('Failed to delete project:', error);
          message.error('Failed to delete project');
        }
      },
    });
  };

  // Save timer settings
  const handleSaveTimerSettings = async (
    capturing: boolean,
    timerSettings?: TimerSettings
  ) => {
    if (!effectiveUid || !selectedView) return;

    try {
      await updateTimerSettings(
        effectiveUid,
        selectedView.projectId,
        selectedView.viewId,
        capturing,
        timerSettings
      );
      // Don't show message here - TimerTab handles toast notifications
      // Don't reload - let the local state persist to avoid resetting user input
      // loadProjects();
    } catch (error) {
      console.error('Failed to save timer settings:', error);
      // Don't show message here - TimerTab handles toast notifications
      throw error; // Re-throw to let TimerTab handle error display
    }
  };

  // Save camera settings (Phase 4)
  const handleSaveCameraSettings = async (cameraSettings: CameraSettings) => {
    if (!effectiveUid || !selectedView) return;

    try {
      await updateCameraSettings(
        effectiveUid,
        selectedView.projectId,
        selectedView.viewId,
        cameraSettings
      );
      // Don't show message here - CameraTab handles toast notifications
    } catch (error) {
      console.error('Failed to save camera settings:', error);
      throw error; // Re-throw to let CameraTab handle error display
    }
  };

  // Bulk Actions handlers
  const handleOpenBulkActions = (project: Project) => {
    setBulkActionsProject(project);
    setBulkCapturing('unchanged');
    setBulkIntervalValue(undefined);
    setBulkIntervalUnit(60);
    setBulkRawCapture('unchanged');
    setBulkActionsModalVisible(true);
  };

  const handleCloseBulkActions = () => {
    setBulkActionsModalVisible(false);
    setBulkActionsProject(null);
    setBulkCapturing('unchanged');
    setBulkIntervalValue(undefined);
    setBulkIntervalUnit(60);
    setBulkRawCapture('unchanged');
  };

  const handleSaveBulkActions = async () => {
    if (!effectiveUid || !bulkActionsProject) return;

    // Calculate interval in seconds
    const intervalSeconds = bulkIntervalValue !== undefined
      ? bulkIntervalValue * bulkIntervalUnit
      : undefined;

    // Convert capturing state
    const capturingValue = bulkCapturing === 'enabled' ? true
      : bulkCapturing === 'paused' ? false
      : undefined;

    // Convert RAW capture state
    const rawEnabledValue = bulkRawCapture === 'enabled' ? true
      : bulkRawCapture === 'disabled' ? false
      : undefined;

    // Check if at least one option is set
    if (capturingValue === undefined && intervalSeconds === undefined && rawEnabledValue === undefined) {
      message.warning('Please set at least one option');
      return;
    }

    // Validate minimum interval
    if (intervalSeconds !== undefined && intervalSeconds < 10) {
      message.error('Minimum interval is 10 seconds');
      return;
    }

    try {
      setBulkActionsSaving(true);
      await updateBulkTimerSettings(effectiveUid, bulkActionsProject.projectId, {
        capturing: capturingValue,
        interval: intervalSeconds,
        rawEnabled: rawEnabledValue,
      });
      message.success('Bulk settings applied successfully');
      handleCloseBulkActions();
    } catch (error) {
      console.error('Failed to apply bulk settings:', error);
      message.error('Failed to apply bulk settings');
    } finally {
      setBulkActionsSaving(false);
    }
  };

  // Project menu
  const getProjectMenu = (project: Project): MenuProps['items'] => [
    {
      key: 'add-view',
      icon: <MaterialIcon name="add" />,
      label: 'Add New View',
      onClick: () => {
        setSelectedProjectId(project.projectId);
        setCreateViewModalVisible(true);
      },
    },
    {
      key: 'bulk-actions',
      icon: <MaterialIcon name="tune" />,
      label: 'Bulk Settings',
      onClick: () => handleOpenBulkActions(project),
    },
    {
      key: 'share',
      icon: <MaterialIcon name="share" />,
      label: 'Share',
      onClick: () => {
        setShareDialogProject(project);
        setShareDialogVisible(true);
      },
    },
    {
      key: 'edit',
      icon: <MaterialIcon name="edit" />,
      label: 'Rename Project',
      onClick: () => {
        setProjectToEdit(project);
        editProjectForm.setFieldsValue({ name: project.name });
        setEditProjectModalVisible(true);
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      icon: <MaterialIcon name="delete" />,
      label: 'Delete Project',
      danger: true,
      onClick: () => handleDeleteProject(project.projectId, project.name),
    },
  ];

  // View actions menu
  const getViewActionsMenu = (view: View): MenuProps['items'] => [
    {
      key: 'rename',
      icon: <MaterialIcon name="edit" />,
      label: 'Rename view',
      onClick: () => {
        setViewToEdit(view);
        editViewForm.setFieldsValue({ name: view.name });
        setEditViewModalVisible(true);
      },
    },
    {
      key: 'copy',
      icon: <MaterialIcon name="content_copy" />,
      label: 'Copy Token',
      onClick: () => handleCopyToken(view.token),
    },
    {
      key: 'regenerate',
      icon: <MaterialIcon name="refresh" />,
      label: 'Regenerate Token',
      onClick: () => handleRegenerateToken(view.userId, view.projectId, view.viewId),
    },
    {
      key: 'view-id',
      icon: <MaterialIcon name="fingerprint" />,
      label: 'Copy View ID',
      onClick: () => {
        const lines = [
          `View: ${view.name}`,
          `Path: users/${view.userId}/projects/${view.projectId}/views/${view.viewId}`,
          `Token: ${view.token}`,
        ];
        navigator.clipboard.writeText(lines.join('\n'));
        message.success('View ID copied');
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      icon: <MaterialIcon name="delete" />,
      label: 'Delete View',
      danger: true,
      onClick: () =>
        handleDeleteView(view.userId, view.projectId, view.viewId, view.name),
    },
  ];

  // Single reveal: hold ONE skeleton until projects/views are loaded AND the
  // latest-photo subscription has fired once for every view — no piecewise
  // flashes. The 4s safety valve force-reveals on slow/degenerate networks.
  const latestPhotosReady = viewIds
    .split(',')
    .filter(Boolean)
    .every((id) => latestPhotos[id] !== undefined);
  const pageReady = !loading && (latestPhotosReady || forceReveal);

  if (!pageReady) {
    return (
      <AppLayout>
        {/* Skeleton mirrors the real structure (project bar + view cards grid)
            so the layout does not jump at reveal */}
        <div className="mb-6 flex items-center justify-between">
          <Title level={2}>Projects</Title>
        </div>
        <div className="flex flex-col gap-6">
          {[0, 1].map((i) => (
            <div key={i}>
              <Card
                className="mb-4"
                styles={{
                  body: { padding: 0 },
                  header: {
                    backgroundColor: 'var(--ant-gray-6)',
                    borderBottom: '1px solid var(--ant-color-split)',
                  },
                }}
                title={
                  <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 1, width: 180 }}
                    style={{ margin: '6px 0' }}
                  />
                }
              />
              <div className="view-cards-grid">
                {[0, 1].map((j) => (
                  <Card
                    key={j}
                    style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    cover={
                      <div
                        className="h-48"
                        style={{ background: 'radial-gradient(ellipse at center, #e0e0e0 0%, #9e9e9e 100%)' }}
                      />
                    }
                  >
                    <Skeleton active title={false} paragraph={{ rows: 3 }} />
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <Title level={2}>Projects</Title>
        {projects.length > 0 && (
          <Button
            type="primary"
            icon={<MaterialIcon name="create_new_folder" />}
            onClick={() => setCreateProjectModalVisible(true)}
          >
            New Project
          </Button>
        )}
      </div>

      {/* Projects List */}
      {projects.length === 0 ? (
        <Empty
          description="No projects yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            icon={<MaterialIcon name="create_new_folder" />}
            onClick={() => setCreateProjectModalVisible(true)}
          >
            Create Your First Project
          </Button>
        </Empty>
      ) : (
        <div className="flex flex-col gap-6">
          {projects.map((project) => (
            <div key={project.projectId}>
              {/* Project Card - Full Width */}
              <Card
                className="mb-4"
                styles={{
                  body: { padding: 0 },
                  header: {
                    backgroundColor: 'var(--ant-gray-6)',
                    borderBottom: '1px solid var(--ant-color-split)',
                  },
                }}
                title={
                  <div className="flex items-center justify-between">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text strong className="text-base" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{project.name}</Text>
                      <Tag
                        icon={<MaterialIcon name="photo_library" size={14} />}
                        style={{ fontWeight: 400 }}
                      >
                        {(viewsByProject[project.projectId]?.reduce((sum, v) => sum + (v.stats?.photoCount ?? 0), 0) ?? 0).toLocaleString()}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {viewsByProject[project.projectId]?.some(v => v.gpsLocation) ? (
                        <Button
                          type="text"
                          icon={<MaterialIcon name="location_on" size={18} style={{ color: '#1890ff' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/map?projectId=${project.projectId}`);
                          }}
                          title="Show all views on map"
                        />
                      ) : (
                        <Button
                          type="text"
                          icon={<MaterialIcon name="location_off" size={18} style={{ color: '#d9d9d9' }} />}
                          disabled
                          title="No positions available"
                        />
                      )}
                      <Dropdown
                        menu={{ items: getProjectMenu(project) }}
                        trigger={['click']}
                        open={openDropdownId === `project:${project.projectId}`}
                        onOpenChange={(open) =>
                          setOpenDropdownId(open ? `project:${project.projectId}` : null)
                        }
                      >
                        <Button type="text" icon={<MaterialIcon name="more_vert" />} />
                      </Dropdown>
                    </div>
                  </div>
                }
              />

              {/* Views Grid - Below Project Card */}
              {viewsByProject[project.projectId]?.length === 0 ? (
                <div className="px-6 pb-6">
                  <Empty
                    description="No views yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button
                      type="primary"
                      size="small"
                      icon={<MaterialIcon name="add" />}
                      onClick={() => {
                        setSelectedProjectId(project.projectId);
                        setCreateViewModalVisible(true);
                      }}
                    >
                      Add View
                    </Button>
                  </Empty>
                </div>
              ) : (
                <div className="view-cards-grid">
                  {viewsByProject[project.projectId]?.map((view) => (
                    <Card
                      key={view.viewId}
                      hoverable
                      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                      cover={
                          <div
                            className="h-48 flex items-center justify-center cursor-pointer overflow-hidden relative"
                            style={{ background: 'radial-gradient(ellipse at center, #e0e0e0 0%, #9e9e9e 100%)' }}
                            onClick={() => navigate(`/photos/${view.viewId}`)}
                          >
                            {latestPhotos[view.viewId] && !imageLoadErrors[view.viewId] && (
                              <img
                                src={latestPhotos[view.viewId]!.urls.thumb400 || latestPhotos[view.viewId]!.urls.thumb200}
                                alt={`Latest photo from ${view.name}`}
                                className="absolute inset-0 w-full h-full object-cover"
                                onLoad={() => {
                                  setImageLoading((prev) => ({
                                    ...prev,
                                    [view.viewId]: false,
                                  }));
                                }}
                                onError={() => {
                                  setImageLoadErrors((prev) => ({
                                    ...prev,
                                    [view.viewId]: true,
                                  }));
                                  setImageLoading((prev) => ({
                                    ...prev,
                                    [view.viewId]: false,
                                  }));
                                }}
                              />
                            )}
                            {/* Show loader only while the image file is actively downloading.
                                The pre-subscription case is handled by the page-level skeleton gate. */}
                            {imageLoading[view.viewId] && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Spin />
                              </div>
                            )}
                            {/* Show empty state when subscription confirmed no photos exist */}
                            {latestPhotos[view.viewId] === null && !imageLoading[view.viewId] && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                                <MaterialIcon name="photo_camera" size={40} style={{ opacity: 0.4 }} />
                                <span className="text-sm mt-2">No photos yet</span>
                              </div>
                            )}
                            {/* Show error state when thumbnail failed to load */}
                            {imageLoadErrors[view.viewId] && !imageLoading[view.viewId] && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                                <MaterialIcon name="broken_image" size={40} style={{ opacity: 0.4 }} />
                                <span className="text-sm mt-2">Failed to load preview</span>
                              </div>
                            )}
                            {/* Top overlay: photo count (left) and last capture time (right) */}
                            <Tag
                              className="overlay-tag absolute top-2 left-2"
                              icon={<MaterialIcon name="photo_library" size={14} />}
                            >
{(view.stats?.photoCount ?? 0).toLocaleString()}
                            </Tag>
                            {view.discoverySessionId && (
                              <div
                                className="absolute bottom-2 left-2 right-2"
                                style={{
                                  background: 'rgba(22, 119, 255, 0.85)',
                                  borderRadius: 6,
                                  padding: '4px 8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  color: '#fff',
                                  fontSize: 12,
                                  lineHeight: 1.3,
                                  backdropFilter: 'blur(4px)',
                                }}
                              >
                                <MaterialIcon name="troubleshoot" size={16} style={{ flexShrink: 0 }} />
                                <span>Camera analysis in progress — capture will resume automatically</span>
                              </div>
                            )}
                            <div className="absolute top-2 right-2 flex gap-1">
                              {latestPhotos[view.viewId]?.capturedAt && (() => {
                                const captureDate = latestPhotos[view.viewId]!.capturedAt.toDate();
                                const isStale = isLastCaptureStale(view, captureDate);
                                return (
                                  <Tag
                                    className={`overlay-tag ${isStale ? 'overlay-tag--stale' : ''}`}
                                    icon={<MaterialIcon name="schedule" size={14} />}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setStatusDrawerView(view);
                                      setStatusDrawerScrollTarget(null);
                                      setStatusDrawerVisible(true);
                                    }}
                                  >
                                    {formatTimeAgo(captureDate)}
                                  </Tag>
                                );
                              })()}
                            </div>
                          </div>
                        }
                        actions={[
                          <Button
                            key="camera"
                            type="text"
                            icon={<MaterialIcon name="settings_photo_camera" size={22} />}
                            disabled={!view.androidStatus}
                            style={{ fontSize: 14, height: 34 }}
                            onClick={() => {
                              setSelectedView(view);
                              setActiveDrawerTab('1');
                              setSettingsDrawerVisible(true);
                            }}
                          >
                            Camera
                          </Button>,
                          <Button
                            key="timer"
                            type="text"
                            icon={<MaterialIcon name="avg_pace" size={22} />}
                            disabled={!view.androidStatus}
                            style={{ fontSize: 14, height: 34 }}
                            onClick={() => {
                              setSelectedView(view);
                              setActiveDrawerTab('2');
                              setSettingsDrawerVisible(true);
                            }}
                          >
                            Timer
                          </Button>,
                          <Button
                            key="status"
                            type="text"
                            icon={<MaterialIcon name="checklist" size={22} />}
                            style={{ fontSize: 14, height: 34 }}
                            onClick={() => {
                              setStatusDrawerView(view);
                              setStatusDrawerScrollTarget(null);
                              setStatusDrawerVisible(true);
                            }}
                          >
                            Status
                          </Button>,
                          <Dropdown
                            key="more"
                            menu={{ items: getViewActionsMenu(view) }}
                            placement="bottomRight"
                            trigger={['click']}
                            open={openDropdownId === `view:${view.viewId}`}
                            onOpenChange={(open) =>
                              setOpenDropdownId(open ? `view:${view.viewId}` : null)
                            }
                          >
                            <Button
                              type="text"
                              icon={<MaterialIcon name="more_vert" size={22} />}
                              style={{ fontSize: 14, height: 34 }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Dropdown>,
                        ]}
                      >
                        <Meta
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{view.name}</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                                <span
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#8c8c8c', fontWeight: 400, fontSize: 13, cursor: 'pointer' }}
                                  title="Click to copy token"
                                  onClick={(e) => { e.stopPropagation(); handleCopyToken(view.token); }}
                                >
                                  <MaterialIcon name="key" size={14} />
                                  {view.token}
                                </span>
                                {view.gpsLocation ? (
                                  <span
                                    style={{ cursor: 'pointer', display: 'inline-flex' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/map?viewId=${view.viewId}`);
                                    }}
                                    title="Show on map"
                                  >
                                    <MaterialIcon name="location_on" size={16} style={{ color: '#1890ff' }} />
                                  </span>
                                ) : (
                                  <span
                                    style={{ cursor: 'pointer', display: 'inline-flex' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setStatusDrawerView(view);
                                      setStatusDrawerScrollTarget('gpsPosition');
                                      setStatusDrawerVisible(true);
                                    }}
                                    title="No position available — click for details"
                                  >
                                    <MaterialIcon name="location_on" size={16} style={{ color: '#d9d9d9' }} />
                                  </span>
                                )}
                                {/* Per-view WhatsApp alerts toggle */}
                                {(() => {
                                  if (!isWhatsappConfigured) {
                                    return (
                                      <span
                                        style={{ cursor: 'pointer', display: 'inline-flex' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setWhatsappSettingsOpen(true);
                                        }}
                                        title="WhatsApp alerts — click to set up"
                                      >
                                        <MaterialIcon name="notifications" size={16} style={{ color: '#d9d9d9' }} />
                                      </span>
                                    );
                                  }
                                  const muted = isMuted(view.viewId);
                                  return (
                                    <span
                                      style={{ cursor: 'pointer', display: 'inline-flex' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setWhatsappToggleView(view);
                                      }}
                                      title="WhatsApp alerts"
                                    >
                                      <MaterialIcon name="notifications" size={16} style={{ color: muted ? '#d9d9d9' : '#1890ff' }} />
                                    </span>
                                  );
                                })()}
                                {/* Per-view share indicator */}
                                {(() => {
                                  const shared = isViewShared(view);
                                  return (
                                    <span
                                      style={{ cursor: 'pointer', display: 'inline-flex' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShareDialogProject(project);
                                        setShareDialogVisible(true);
                                      }}
                                      title={shared ? 'Shared — click to manage' : 'Not shared — click to configure'}
                                    >
                                      <MaterialIcon
                                        name="share"
                                        size={16}
                                        style={{ color: shared ? '#1890ff' : '#d9d9d9' }}
                                      />
                                    </span>
                                  );
                                })()}
                              </span>
                            </div>
                          }
                          description={
                            <ViewCardDescription
                              view={view}
                              onOfflineClick={() => {
                                setStatusDrawerView(view);
                                setStatusDrawerScrollTarget(null);
                                setStatusDrawerVisible(true);
                              }}
                              onUnpairedClick={() => {
                                setSelectedView(view);
                                setActiveDrawerTab('1');
                                setSettingsDrawerVisible(true);
                              }}
                              onCapturingClick={() => {
                                setSelectedView(view);
                                setActiveDrawerTab('2');
                                setSettingsDrawerVisible(true);
                              }}
                              onSurvivalClick={() => {
                                setStatusDrawerView(view);
                                setStatusDrawerScrollTarget(null);
                                setStatusDrawerVisible(true);
                              }}
                              onIconClick={(itemId) => {
                                setStatusDrawerView(view);
                                setStatusDrawerScrollTarget(itemId);
                                setStatusDrawerVisible(true);
                              }}
                              onCameraSettingsClick={() => {
                                setSelectedView(view);
                                setActiveDrawerTab('1');
                                setSettingsDrawerVisible(true);
                              }}
                            />
                          }
                        />
                      </Card>
                  ))}
                  {/* Add View placeholder */}
                  <div
                    className="flex flex-col items-center justify-center cursor-pointer rounded-lg"
                    style={{
                      minHeight: 200,
                      background: '#ffffff',
                      border: '2px dashed #d9d9d9',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1677ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d9d9d9'; }}
                    onClick={() => {
                      setSelectedProjectId(project.projectId);
                      setCreateViewModalVisible(true);
                    }}
                  >
                    <MaterialIcon name="add_a_photo" size={48} style={{ color: '#bfbfbf' }} />
                    <span className="text-sm mt-2" style={{ color: '#8c8c8c', fontWeight: 500 }}>Add View</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      <Modal
        title="Create New Project"
        open={createProjectModalVisible}
        onCancel={() => {
          setCreateProjectModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={creatingProject}
        styles={MODAL_BLUR_STYLES}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateProject}>
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: 'Please enter project name' }]}
          >
            <Input placeholder="e.g., Construction Site A" maxLength={40} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create View Modal */}
      <Modal
        title="Create New View"
        open={createViewModalVisible}
        onCancel={() => {
          setCreateViewModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={creatingView}
        styles={MODAL_BLUR_STYLES}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateView}>
          <Form.Item
            name="name"
            label="View Name"
            rules={[{ required: true, message: 'Please enter view name' }]}
          >
            <Input placeholder="e.g., North Facade" maxLength={40} />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Created - Pairing Instructions */}
      <Modal
        title="View created successfully!"
        open={!!newViewToken}
        onCancel={() => setNewViewToken(null)}
        footer={
          <Button
            type="primary"
            onClick={() => setNewViewToken(null)}
          >
            Got it
          </Button>
        }
        styles={MODAL_BLUR_STYLES}
      >
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          {/* App screenshot with arrow */}
          <div style={{ flexShrink: 0, position: 'relative' }}>
            <img
              src="/img/pairing-screen.png"
              alt="Masterlapse app pairing screen"
              style={{
                width: 160,
                borderRadius: 12,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              }}
            />
            {/* Arrow pointing to token input area */}
            <div
              style={{
                position: 'absolute',
                top: '30%',
                right: -18,
                color: '#1677ff',
                fontSize: 28,
                lineHeight: 1,
                animation: 'pairingArrowBounce 1s ease-in-out infinite',
              }}
            >
              <MaterialIcon name="arrow_back" size={28} />
            </div>
            <style>{`
              @keyframes pairingArrowBounce {
                0%, 100% { transform: translateX(0); }
                50% { transform: translateX(-6px); }
              }
            `}</style>
          </div>

          {/* Instructions */}
          <div style={{ flex: 1 }}>
            <p style={{ marginBottom: 12 }}>
              To start capturing, pair your Android device with this view:
            </p>
            <ol style={{ paddingLeft: 20, marginBottom: 16 }}>
              <li>Open the <strong>Masterlapse</strong> app on your device</li>
              <li>Enter the token below on the pairing screen</li>
            </ol>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: '12px 16px',
              }}
            >
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 20,
                  letterSpacing: 4,
                  fontWeight: 700,
                  userSelect: 'all',
                }}
              >
                {newViewToken}
              </span>
              <MaterialIcon
                name="content_copy"
                size={20}
                style={{
                  color: '#8c8c8c',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'color 0.2s, transform 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#1677ff';
                  e.currentTarget.style.transform = 'scale(1.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#8c8c8c';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.15)';
                }}
                onClick={async () => {
                  if (!newViewToken) return;
                  const ok = await copyToClipboard(newViewToken);
                  if (ok) message.success('Token copied to clipboard');
                  else message.error('Failed to copy token');
                }}
              />
            </div>
            <p style={{ marginTop: 12, fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
              Don't have the app yet?{' '}
              <a href="https://www.masterlapse.com/get-started/" target="_blank" rel="noopener noreferrer">
                Download Masterlapse
              </a>
            </p>
          </div>
        </div>
      </Modal>

      {/* Edit View Modal */}
      <Modal
        title="Rename View"
        open={editViewModalVisible}
        onCancel={() => {
          setEditViewModalVisible(false);
          setViewToEdit(null);
          editViewForm.resetFields();
        }}
        onOk={() => editViewForm.submit()}
        styles={MODAL_BLUR_STYLES}
      >
        <Form form={editViewForm} layout="vertical" onFinish={handleEditViewSubmit}>
          <Form.Item
            name="name"
            label="View Name"
            rules={[{ required: true, message: 'Please enter view name' }]}
          >
            <Input placeholder="e.g., North Facade" maxLength={40} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Move View Modal */}
      <Modal
        title="Move to project"
        open={moveViewModalVisible}
        onCancel={() => {
          setMoveViewModalVisible(false);
          setViewToMove(null);
          setMoveTargetProjectId(null);
        }}
        onOk={handleMoveView}
        okText="Move"
        okButtonProps={{ disabled: !moveTargetProjectId }}
        confirmLoading={movingView}
        styles={MODAL_BLUR_STYLES}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Move <Text strong>{viewToMove?.name}</Text> to:
          </Text>
        </div>
        <Select
          placeholder="Select destination project"
          value={moveTargetProjectId}
          onChange={(val) => setMoveTargetProjectId(val)}
          style={{ width: '100%' }}
        >
          {projects
            .filter((p) => p.projectId !== viewToMove?.projectId)
            .map((p) => (
              <Select.Option key={p.projectId} value={p.projectId}>
                {p.name}
              </Select.Option>
            ))}
        </Select>
      </Modal>

      {/* Edit Project Modal */}
      <Modal
        title="Rename Project"
        open={editProjectModalVisible}
        onCancel={() => {
          setEditProjectModalVisible(false);
          setProjectToEdit(null);
          editProjectForm.resetFields();
        }}
        onOk={() => editProjectForm.submit()}
        styles={MODAL_BLUR_STYLES}
      >
        <Form form={editProjectForm} layout="vertical" onFinish={handleEditProjectSubmit}>
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: 'Please enter project name' }]}
          >
            <Input placeholder="e.g., Building Construction" maxLength={40} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Settings Modal */}
      <Modal
        title={`Bulk Settings - ${bulkActionsProject?.name || ''}`}
        open={bulkActionsModalVisible}
        onCancel={handleCloseBulkActions}
        onOk={handleSaveBulkActions}
        okText="Apply"
        confirmLoading={bulkActionsSaving}
        okButtonProps={{
          disabled: bulkCapturing === 'unchanged' && bulkIntervalValue === undefined && bulkRawCapture === 'unchanged',
        }}
        styles={MODAL_BLUR_STYLES}
      >
        <div className="flex flex-col gap-6 py-4">
          {/* Warning */}
          <div className="flex items-center gap-2">
            <MaterialIcon name="warning" style={{ color: '#faad14' }} />
            <Text type="secondary">Settings below will be applied to all views of this project</Text>
          </div>

          {/* Capture */}
          <div className="flex items-center justify-between">
            <Text>Capture</Text>
            <Segmented
              value={bulkCapturing}
              onChange={(val) => setBulkCapturing(val as 'unchanged' | 'enabled' | 'paused')}
              options={[
                { label: '—', value: 'unchanged' },
                { label: 'Enabled', value: 'enabled' },
                { label: 'Paused', value: 'paused' },
              ]}
            />
          </div>

          {/* Interval */}
          <div className="flex items-center justify-between">
            <Text>Interval</Text>
            <Space.Compact>
              <InputNumber
                min={1}
                max={999}
                placeholder="—"
                value={bulkIntervalValue}
                onChange={(value) => setBulkIntervalValue(value ?? undefined)}
                style={{ width: 80 }}
              />
              <Select
                value={bulkIntervalUnit}
                onChange={(val) => setBulkIntervalUnit(val)}
                style={{ width: 100 }}
              >
                <Select.Option value={1}>seconds</Select.Option>
                <Select.Option value={60}>minutes</Select.Option>
                <Select.Option value={3600}>hours</Select.Option>
              </Select>
            </Space.Compact>
          </div>

          {/* RAW Capture */}
          <div className="flex items-center justify-between">
            <Text>RAW Capture</Text>
            <Segmented
              value={bulkRawCapture}
              onChange={(val) => setBulkRawCapture(val as 'unchanged' | 'enabled' | 'disabled')}
              options={[
                { label: '—', value: 'unchanged' },
                { label: 'Disabled', value: 'disabled' },
              ]}
            />
          </div>
        </div>
      </Modal>

      {/* Share Dialog */}
      {shareDialogProject && (
        <ShareDialog
          open={shareDialogVisible}
          project={shareDialogProject}
          views={viewsByProject[shareDialogProject.projectId] || []}
          onClose={() => {
            setShareDialogVisible(false);
            setShareDialogProject(null);
          }}
        />
      )}

      {/* Test Shot Viewer */}
      {testShotView && (
        <TestShotViewer
          view={testShotView}
          onClose={() => setTestShotView(null)}
        />
      )}

      {/* WhatsApp alerts enable/disable confirmation */}
      <Modal
        open={!!whatsappToggleView}
        onCancel={() => setWhatsappToggleView(null)}
        onOk={async () => {
          if (whatsappToggleView) {
            const muted = isMuted(whatsappToggleView.viewId);
            await setMuted(whatsappToggleView.viewId, !muted);
            setWhatsappToggleView(null);
          }
        }}
        okText={whatsappToggleView && isMuted(whatsappToggleView.viewId) ? 'Enable' : 'Disable'}
        okButtonProps={{
          danger: whatsappToggleView ? !isMuted(whatsappToggleView.viewId) : false,
        }}
        title={
          whatsappToggleView && isMuted(whatsappToggleView.viewId)
            ? 'Enable WhatsApp alerts'
            : 'Disable WhatsApp alerts'
        }
        styles={MODAL_BLUR_STYLES}
      >
        {whatsappToggleView && (
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>{whatsappToggleView.name}</strong>
            </p>
            {isMuted(whatsappToggleView.viewId) ? (
              <p style={{ color: '#595959' }}>
                You will receive WhatsApp notifications for this view — including connectivity changes, power events, and temperature alerts.
              </p>
            ) : (
              <p style={{ color: '#595959' }}>
                WhatsApp notifications for this view will be silenced. You will no longer receive alerts for connectivity, power, or temperature events.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* WhatsApp Settings — opened when clicking notifications icon while not configured */}
      <WhatsAppSettings
        open={whatsappSettingsOpen}
        onClose={() => setWhatsappSettingsOpen(false)}
      />

      <SettingsDrawer
        open={settingsDrawerVisible}
        view={selectedView}
        activeTab={activeDrawerTab}
        onClose={() => {
          setSettingsDrawerVisible(false);
          setSelectedView(null);
          setHighlightFieldId(null);
          setActiveDrawerTab('1');
        }}
        onTestShot={(v) => setTestShotView(v)}
        onSaveCameraSettings={handleSaveCameraSettings}
        onSaveTimerSettings={handleSaveTimerSettings}
        highlightFieldId={highlightFieldId}
        onHighlightComplete={() => setHighlightFieldId(null)}
      />

      {/* Status Detail Drawer */}
      <StatusDetailDrawer
        open={statusDrawerVisible}
        onClose={() => {
          setStatusDrawerVisible(false);
          setStatusDrawerView(null);
          setStatusDrawerScrollTarget(null);
          setHistoryFilterType('all');
        }}
        androidStatus={statusDrawerView?.androidStatus}
        cameraSettings={statusDrawerView?.settings?.camera}
        cameraMode={statusDrawerView?.settings?.camera?.mode ?? 'internal'}
        token={statusDrawerView?.token}
        gpsLocation={statusDrawerView?.gpsLocation}
        gpsError={statusDrawerView?.gpsError}
        viewId={statusDrawerView?.viewId}
        scrollToItemId={statusDrawerScrollTarget}
        userId={statusDrawerView?.userId}
        projectId={statusDrawerView?.projectId}
        otaAutoUpdate={statusDrawerView?.settings?.otaAutoUpdate}
        view={statusDrawerView}
        historyFilterType={historyFilterType}
        onHistoryFilterChange={setHistoryFilterType}
      />
    </AppLayout>
  );
}
